import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Calendar as BigCalendar,
  dateFnsLocalizer,
  Views,
} from "react-big-calendar";
import {
  add,
  endOfMonth,
  format,
  getDay,
  parse,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { enUS } from "date-fns/locale";
import {
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Filter,
  TriangleAlert,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { AppointmentEditorDialog } from "@/components/AppointmentEditorDialog";
import BoardWorkspace from "@/components/BoardWorkspace";
import { Button } from "@/components/ui/button";
import "react-big-calendar/lib/css/react-big-calendar.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});
const eventTypes = ["", "appointment", "meeting", "inspection", "reminder", "other"];
const CALENDAR_COMPONENTS = { event: AppointmentEvent };
const CALENDAR_VIEWS = [Views.MONTH, Views.WEEK, Views.AGENDA];

const newDefaultDate = (date) => {
  const next = new Date(date);
  next.setHours(9, 0, 0, 0);
  return next;
};

const scheduleRange = (date) => ({
  start_date: format(startOfMonth(date), "yyyy-MM-dd"),
  end_date: format(endOfMonth(date), "yyyy-MM-dd"),
});

const friendlyType = (type) =>
  type ? `${type.charAt(0).toUpperCase()}${type.slice(1)}` : "All types";

export default function AppointmentsCalendarPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState(Views.MONTH);
  const [appointments, setAppointments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editorDate, setEditorDate] = useState(newDefaultDate(new Date()));
  const [editorOpen, setEditorOpen] = useState(false);
  const [filters, setFilters] = useState({
    assigned_to_me: false,
    creator_user_id: "",
    event_type: "",
    participant_user_id: "",
    project_id: "",
  });

  const load = useCallback(async () => {
    const response = await axios.get(`${API}/appointments`, {
      params: { ...scheduleRange(date), ...filters },
    });
    setAppointments(response.data.appointments);
  }, [date, filters]);

  useEffect(() => {
    load().catch((error) => {
      toast.error(
        error.response?.data?.detail || "Appointments could not be loaded.",
      );
    });
  }, [load]);

  useEffect(() => {
    const loadReferences = async () => {
      const [people, bootstrap] = await Promise.all([
        axios.get(`${API}/operations/assignable-users`),
        axios.get(`${API}/operations/bootstrap`),
      ]);
      setUsers(people.data.users || []);
      setProjects(bootstrap.data.projects || []);
    };
    loadReferences().catch(() => {
      toast.error("Appointment filters could not be loaded.");
    });
  }, []);

  const events = useMemo(() => {
    const appointmentEvents = appointments.map((appointment) => ({
        allDay: appointment.all_day,
        end: new Date(appointment.ends_at),
        id: appointment.id,
        resource: appointment,
        start: new Date(appointment.starts_at),
        title: appointment.title,
      }));
    return appointmentEvents;
  }, [appointments]);

  const openNew = (selectedDate = date) => {
    setEditingAppointment(null);
    setEditorDate(newDefaultDate(selectedDate));
    setEditorOpen(true);
  };
  const handleSelectEvent = useCallback((event) => {
    if (event.resource.is_project_due) {
      navigate(`/projects/${event.resource.project_id}`);
      return;
    }
    setEditingAppointment(event.resource);
    setEditorDate(event.start);
    setEditorOpen(true);
  }, [navigate]);
  const handleEditorOpenChange = useCallback((isOpen) => {
    setEditorOpen(isOpen);
    if (!isOpen) {
      setEditingAppointment(null);
    }
  }, []);

  const saveAppointment = async (payload) => {
    try {
      if (editingAppointment?.id) {
        await axios.patch(`${API}/appointments/${editingAppointment.id}`, payload);
        toast.success("Appointment updated.");
      } else {
        await axios.post(`${API}/appointments`, payload);
        toast.success("Appointment created.");
      }
      setEditorOpen(false);
      setEditingAppointment(null);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Appointment could not be saved.",
      );
      throw error;
    }
  };

  const deleteAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success("Appointment deleted.");
      setEditorOpen(false);
      setEditingAppointment(null);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Appointment could not be deleted.",
      );
      throw error;
    }
  };

  const shiftCalendar = (amount) => setDate((current) => add(current, amount));
  const updateFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));

  return (
    <BoardWorkspace
      actions={
        <Button
          data-testid="appointments-create-button"
          onClick={() => openNew(date)}
          type="button"
        >
          <CalendarPlus size={16} />
          New appointment
        </Button>
      }
      eyebrow="Company coordination"
      headingTestId="appointments-heading"
      subtitle="Appointments coordinate people; Project Due items are read-only reminders and never change the Project Stage."
      title="Appointments Calendar"
    >
      <section className="appointments-workspace" data-testid="appointments-workspace">
        <div className="appointments-toolbar" data-testid="appointments-toolbar">
          <div className="appointments-navigation">
            <button
              data-testid="appointments-previous-button"
              onClick={() => shiftCalendar(view === Views.MONTH ? { months: -1 } : { weeks: -1 })}
              type="button"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <button
              data-testid="appointments-today-button"
              onClick={() => setDate(new Date())}
              type="button"
            >
              Today
            </button>
            <button
              data-testid="appointments-next-button"
              onClick={() => shiftCalendar(view === Views.MONTH ? { months: 1 } : { weeks: 1 })}
              type="button"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
          <strong data-testid="appointments-range-label">
            {format(date, view === Views.MONTH ? "MMMM yyyy" : "MMMM d, yyyy")}
          </strong>
          <div className="appointments-view-switcher">
            {[Views.MONTH, Views.WEEK, Views.AGENDA].map((calendarView) => (
              <button
                className={view === calendarView ? "active" : ""}
                data-testid={`appointments-view-${calendarView}`}
                key={calendarView}
                onClick={() => setView(calendarView)}
                type="button"
              >
                {calendarView}
              </button>
            ))}
          </div>
        </div>
        <section className="appointments-filter-bar" data-testid="appointments-filters">
          <div className="appointments-filter-heading">
            <Filter size={15} />
            <span>Filter appointments</span>
          </div>
          <label htmlFor="appointment-filter-type">
            Type
            <select
              data-testid="appointment-filter-type"
              id="appointment-filter-type"
              onChange={(event) => updateFilter("event_type", event.target.value)}
              value={filters.event_type}
            >
              {eventTypes.map((type) => (
                <option key={type || "all"} value={type}>
                  {friendlyType(type)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="appointment-filter-project">
            Project
            <select
              data-testid="appointment-filter-project"
              id="appointment-filter-project"
              onChange={(event) => updateFilter("project_id", event.target.value)}
              value={filters.project_id}
            >
              <option value="">All projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {`${project.project_code} · ${project.name}`}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="appointment-filter-participant">
            Participant
            <select
              data-testid="appointment-filter-participant"
              id="appointment-filter-participant"
              onChange={(event) =>
                updateFilter("participant_user_id", event.target.value)
              }
              value={filters.participant_user_id}
            >
              <option value="">Anyone</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="appointment-filter-creator">
            Created by
            <select
              data-testid="appointment-filter-creator"
              id="appointment-filter-creator"
              onChange={(event) =>
                updateFilter("creator_user_id", event.target.value)
              }
              value={filters.creator_user_id}
            >
              <option value="">Anyone</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </label>
          <label className="appointments-mine-filter">
            <input
              checked={filters.assigned_to_me}
              data-testid="appointment-filter-assigned-to-me"
              onChange={(event) =>
                updateFilter("assigned_to_me", event.target.checked)
              }
              type="checkbox"
            />
            Assigned to me
          </label>
        </section>
        <section className="appointments-calendar-panel" data-testid="appointments-calendar-panel">
          <BigCalendar
            components={CALENDAR_COMPONENTS}
            date={date}
            events={events}
            localizer={localizer}
            onNavigate={setDate}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={(slot) => openNew(slot.start)}
            onView={setView}
            selectable
            toolbar={false}
            view={view}
            views={CALENDAR_VIEWS}
          />
          {events.length === 0 && (
            <div className="appointments-empty-state" data-testid="appointments-empty-state">
              <CalendarPlus size={20} />
              <strong>No appointments in this view</strong>
              <span>Create an appointment or adjust the filters.</span>
            </div>
          )}
        </section>
      </section>
      <AppointmentEditorDialog
        appointment={editingAppointment}
        defaultDate={editorDate}
        onDelete={deleteAppointment}
        onOpenChange={handleEditorOpenChange}
        onSave={saveAppointment}
        open={editorOpen}
        projects={projects}
        users={users}
      />
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}

function AppointmentEvent({ event }) {
  const appointment = event.resource;
  if (appointment.is_project_due) {
    return (
      <span
        className="appointment-calendar-event appointment-event-project-due"
        data-testid={`project-due-event-${appointment.project_id}`}
      >
        <strong>{event.title}</strong>
        <small>{appointment.project_name}</small>
      </span>
    );
  }
  return (
    <span
      className={`appointment-calendar-event appointment-event-${appointment.event_type}`}
      data-testid={`appointment-event-${appointment.id}`}
    >
      {appointment.overlap_user_ids.length > 0 && (
        <TriangleAlert
          aria-label="Participant overlap"
          data-testid={`appointment-overlap-${appointment.id}`}
          size={13}
        />
      )}
      <strong>{event.title}</strong>
      {appointment.project_code && <small>{appointment.project_code}</small>}
    </span>
  );
}